// src/components/FileGrid.js
import React, { useState, useEffect, useRef } from 'react';
import { FixedSizeList as List } from 'react-window';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  Box,
  TableSortLabel,
  Chip,
  Paper,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  VideoFile as VideoIcon,
  Image as ImageIcon,
  Description as DocumentIcon,
  ArrowUpward as ArrowUpwardIcon,
  Layers as LayersIcon,
} from '@mui/icons-material';

// ===== Utility Functions =====

/**
 * Formats file size into human readable format
 * @param {number} size - Size in bytes
 * @returns {string} Formatted size with units
 */
const formatSize = (size) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(2)} MB`;
  return `${(size / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

/**
 * Extracts timestamp from SuprArtData filename
 * @param {string} fileName - Name of the file
 * @returns {number} Timestamp in milliseconds or 0 if not found
 */
const getTimestampFromFileName = (fileName) => {
  const match = fileName.match(/SuprArtData-(\d{13})/);
  return match ? parseInt(match[1], 10) : 0;
};

/**
 * Formats a date to MM/DD/YYYY HH:MM:SS AM/PM
 * @param {Date} date - Date object
 * @returns {string} Formatted date string
 */
const formatDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // Convert to 12-hour format

  return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${ampm}`;
};

/**
 * Formats SuprArtData filename into readable date format
 * @param {string} fileName - Name of the file
 * @returns {string} Formatted date string or original filename
 */
const formatFileName = (fileName) => {
  // First check for SuprArtData pattern (supports .json, .suprs, and .supr)
  const suprArtMatch = fileName.match(/SuprArtData-0*(\d{13})\.(json|suprs|supr)/);
  if (suprArtMatch) {
    try {
      const timestamp = parseInt(suprArtMatch[1], 10);
      if (!isNaN(timestamp)) {
        const date = new Date(timestamp);
        if (date.toString() !== 'Invalid Date') {
          return formatDate(date);
        }
      }
    } catch (error) {
      // console.error('Error formatting filename:', error);
    }
  }

  // Check for video file pattern
  const videoMatch = fileName.match(/(.*)-(\d{13})\.mp4$/);
  if (videoMatch) {
    try {
      const [, folderName, timestamp] = videoMatch;
      const date = new Date(parseInt(timestamp, 10));
      if (date.toString() !== 'Invalid Date') {
        return `${folderName} - ${formatDate(date)}`;
      }
    } catch (error) {
      // console.error('Error formatting video filename:', error);
    }
  }

  return fileName;
};

/**
 * Get appropriate icon for file type while using the bucket color theme
 * @param {string} fileName - Name of the file
 * @param {string} bucketType - Current bucket type
 * @returns {JSX.Element} Icon component with appropriate styling
 */
const getFileIcon = (fileName, bucketType) => {
  const lowerName = fileName.toLowerCase();
  const iconColor = getBucketColor(bucketType);

  if (lowerName.endsWith('.supr')) {
    return <LayersIcon sx={{ color: `${iconColor}.main` }} />; // Delta file icon
  } else if (lowerName.endsWith('.json') || lowerName.endsWith('.suprs')) {
    return <FileIcon sx={{ color: `${iconColor}.main` }} />;
  } else if (lowerName.endsWith('.mp4') || lowerName.endsWith('.mov') || lowerName.endsWith('.avi')) {
    return <VideoIcon sx={{ color: `${iconColor}.main` }} />;
  } else if (lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg') || lowerName.endsWith('.png') || lowerName.endsWith('.gif')) {
    return <ImageIcon sx={{ color: `${iconColor}.main` }} />;
  } else if (lowerName.endsWith('.pdf') || lowerName.endsWith('.doc') || lowerName.endsWith('.docx') || lowerName.endsWith('.txt')) {
    return <DocumentIcon sx={{ color: `${iconColor}.main` }} />;
  }

  return <FileIcon sx={{ color: `${iconColor}.main` }} />;
};

/**
 * Get bucket color theme name
 * @param {string} bucketType 
 * @returns {string} Theme color name
 */
const getBucketColor = (bucketType) => {
  switch(bucketType) {
    case 'live': return 'primary';
    case 'artwork': return 'secondary';
    case 'video': return 'success';
    case 'profile': return 'info';
    default: return 'primary';
  }
};

/**
 * Virtualized Row Component for desktop table
 */
const VirtualizedRow = React.memo(({ index, style, data }) => {
  const { 
    items, 
    selectedFiles, // eslint-disable-line no-unused-vars
    onSelect, 
    onFolderClick, 
    bucketType,
    cellStyles,
    isSelected,
    getDocumentName,
    getTimeDisplay,
    formatSize,
    getFileIcon
  } = data;
  
  const item = items[index];
  const isFolder = item.type === 'folder';
  
  return (
    <div style={{...style, padding: '8px 0'}}>
      <TableRow
        hover
        selected={isSelected(item)}
        onClick={(e) => {
          if (e.target.type === 'checkbox' || e.target.className.includes('MuiCheckbox')) {
            return;
          }
          if (isFolder) {
            if (e.shiftKey) {
              e.preventDefault();
              onSelect(item, e);
            } else {
              onFolderClick(item, e);
            }
          } else {
            onSelect(item, e);
          }
        }}
        sx={{
          ...cellStyles.tableRow,
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          borderRadius: 1,
          backgroundColor: 'background.paper',
        }}
      >
        <TableCell 
          padding="checkbox" 
          sx={{
            ...cellStyles.checkboxCell,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Checkbox
            checked={isSelected(item)}
            onChange={(e) => {
              e.stopPropagation();
              const syntheticEvent = {
                ...e,
                shiftKey: e.nativeEvent.shiftKey
              };
              onSelect(item, syntheticEvent);
            }}
            sx={{
              color: 'text.disabled',
              '&.Mui-checked': {
                color: `${getBucketColor(bucketType)}.main`,
              },
            }}
          />
        </TableCell>
        
        {/* Document Name */}
        <TableCell sx={{
          ...cellStyles.documentNameCell,
          flex: '1 1 auto',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}>
          <Box sx={isFolder ? cellStyles.folderCell : cellStyles.fileCell}>
            {isFolder ? (
              <FolderIcon sx={{ 
                color: item.isArchive ? '#95A5A6' : `${getBucketColor(bucketType)}.main`,
                transition: 'transform 0.2s ease',
                transform: 'scale(1.2)',
                flexShrink: 0,
                opacity: item.isArchive ? 0.8 : 1,
              }} />
            ) : (
              getFileIcon(item.name, bucketType)
            )}
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: isFolder ? 500 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
                // Use ellipsis for single line on larger screens, wrap on smaller
                whiteSpace: { xs: 'normal', md: 'nowrap' },
                lineHeight: { xs: 1.3, sm: 1.5 },
                fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                maxWidth: '100%',
                color: item.isArchive ? '#7F8C8D' : 'inherit',
                fontStyle: item.isArchive ? 'italic' : 'normal',
              }}
            >
              {isFolder ? item.name : getDocumentName(item)}
            </Typography>
          </Box>
        </TableCell>
        
        {/* Time */}
        <TableCell sx={{
          ...cellStyles.timeCell,
          width: { xs: '180px', sm: '220px', md: '220px' },
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
        }}>
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
            }}
          >
            {isFolder ? '' : getTimeDisplay(item)}
          </Typography>
        </TableCell>

        {/* Size */}
        <TableCell sx={{
          ...cellStyles.sizeCell,
          width: { xs: '100px', sm: '120px' },
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
        }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
            }}
          >
            {isFolder ? (item.size ? formatSize(item.size) : '--') : formatSize(item.size)}
          </Typography>
        </TableCell>
      </TableRow>
    </div>
  );
});

/**
 * Virtualized Row Component for mobile list
 */
const VirtualizedMobileRow = React.memo(({ index, style, data }) => {
  const { 
    items, 
    selectedFiles, // eslint-disable-line no-unused-vars
    onSelect, 
    onFolderClick, 
    bucketType,
    isSelected,
    getDocumentName,
    getTimeDisplay,
    formatSize,
    getFileIcon
  } = data;
  
  const item = items[index];
  const isFolder = item.type === 'folder';
  
  return (
    <div style={style}>
      <ListItem 
        disablePadding
        secondaryAction={
          <Checkbox
            checked={isSelected(item)}
            onChange={(e) => {
              e.stopPropagation();
              onSelect(item, e);
            }}
            edge="end"
            sx={{
              color: 'text.disabled',
              padding: 1.5,
              '&.Mui-checked': {
                color: `${getBucketColor(bucketType)}.main`,
              },
            }}
          />
        }
      >
        <ListItemButton 
          onClick={(e) => {
            if (isFolder) {
              onFolderClick(item, e);
            } else {
              onSelect(item, e);
            }
          }}
          dense
          selected={!isFolder && isSelected(item)}
          sx={{ 
            py: 1.5,
            borderRadius: 1,
            '&.Mui-selected': {
              backgroundColor: 'rgba(88, 101, 242, 0.12)',
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            {isFolder ? (
              <FolderIcon 
                sx={{ 
                  color: item.isArchive ? '#95A5A6' : `${getBucketColor(bucketType)}.main`,
                  fontSize: '1.5rem',
                  opacity: item.isArchive ? 0.8 : 1,
                }}
              />
            ) : (
              getFileIcon(item.name, bucketType)
            )}
          </ListItemIcon>
          <ListItemText 
            primary={isFolder ? item.name : getDocumentName(item)}
            secondary={
              !isFolder && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {getTimeDisplay(item).split(' at ')[0]}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', ml: 1 }}>
                    {formatSize(item.size)}
                  </Typography>
                </Box>
              )
            }
            primaryTypographyProps={{
              variant: isFolder ? 'body1' : 'body2',
              fontWeight: isFolder ? 500 : 400,
              noWrap: true,
              sx: {
                color: item.isArchive ? '#7F8C8D' : 'inherit',
                fontStyle: item.isArchive ? 'italic' : 'normal',
              }
            }}
            sx={{ mr: 1 }}
          />
        </ListItemButton>
      </ListItem>
      <Divider variant="inset" component="div" />
    </div>
  );
});

/**
 * Main FileGrid component
 */
const FileGrid = React.memo(({
  files: filesProp,
  folders,
  selectedFiles,
  onSelect,
  onFolderClick,
  onSelectAll,
  allSelected,
  sortConfig,
  onSort,
  bucketType,
  loading,
  selectedGroup = 'all'
}) => {

  // State for dynamic height calculation
  const [listHeight, setListHeight] = useState(600);
  const [mobileListHeight, setMobileListHeight] = useState(400);
  const containerRef = useRef(null);
  const mobileContainerRef = useRef(null);
  
  // Calculate container height on mount and resize
  useEffect(() => {
    const calculateHeight = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setListHeight(rect.height);
      }
      if (mobileContainerRef.current) {
        const rect = mobileContainerRef.current.getBoundingClientRect();
        setMobileListHeight(rect.height);
      }
    };
    
    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  // Move getDocumentName function here, inside the component
  const getDocumentName = (file) => {
    if (bucketType === 'profile') {
      // For profile bucket, just return the filename without any processing
      return file.name;
    }
    
    // For artwork bucket, continue to check if the file has a proper document name or default to 'Untitled'
    if (bucketType === 'artwork') {
      // First check for proper document name in metadata
      if (file.metadata && 
          (file.metadata['document-name'] || file.metadata.document_name) && 
          file.metadata['document-name'] !== 'Untitled' && 
          file.metadata.document_name !== 'Untitled') {
        if (file.metadata['document-name']) return file.metadata['document-name'];
        if (file.metadata.document_name) return file.metadata.document_name;
      }
      
      // Check if the name appears to be a default date (like the January 1970 dates)
      if (file.name.includes('1970') && 
          (file.name.includes('AM') || file.name.includes('PM'))) {
        return 'Untitled';
      }
      
      // Return 'Untitled' for artwork files without proper names
      return 'Untitled';
    }
    
    // Original logic for other bucket types
    if (file.metadata) {
      if (file.metadata['document-name']) return file.metadata['document-name'];
      if (file.metadata.document_name) return file.metadata.document_name;
      
      // For SuprArtData files, try to extract a nice name from timestamp
      const timestampMatch = file.name.match(/SuprArtData-(\d{13})/);
      if (timestampMatch) {
        const timestamp = parseInt(timestampMatch[1], 10);
        try {
          const date = new Date(timestamp);
          if (date.toString() !== 'Invalid Date') {
            return date.toLocaleString(undefined, {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              second: '2-digit',
              hour12: true
            });
          }
        } catch (error) {
          // console.error('Error formatting timestamp:', error);
        }
      }
    }
    
    // Fallback to file name without extension for non-SuprArt files
    if (!file.name.includes('SuprArtData-')) {
      return file.name.replace(/\.\w+$/, '');
    }
    
    // Fallback to 'Untitled'
    return 'Untitled';
  };

  // Sort files with improved handling for profile bucket
  const sortedFiles = [...filesProp].sort((a, b) => {
    const direction = sortConfig.direction === 'desc' ? 1 : -1;
    
    // Handle sorting by document name (Name column)
    if (sortConfig.field === 'document_name') {
      // For profile bucket, sort by filename directly
      if (bucketType === 'profile') {
        return direction * a.name.localeCompare(b.name);
      }
      
      // For other buckets, use metadata or filename
      const nameA = getDocumentName(a);
      const nameB = getDocumentName(b);
      return direction * nameA.localeCompare(nameB);
    }
    
    // Handle sorting by time (Time column)
    if (sortConfig.field === 'name') {
      let timeA, timeB;
      
      if (bucketType === 'profile') {
        // For profile bucket, use lastModified
        timeA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
        timeB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
      } else if (bucketType === 'video') {
        // For video bucket, extract timestamp from filename or use lastModified
        const videoMatchA = a.name.match(/-(\d{13})\.mp4$/);
        const videoMatchB = b.name.match(/-(\d{13})\.mp4$/);
        
        if (videoMatchA) {
          timeA = parseInt(videoMatchA[1], 10);
        } else {
          timeA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
        }
        
        if (videoMatchB) {
          timeB = parseInt(videoMatchB[1], 10);
        } else {
          timeB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
        }
      } else {
        // For live and artwork buckets
        // First try to get timestamp from metadata
        if (a.metadata && a.metadata['original-timestamp']) {
          timeA = parseInt(a.metadata['original-timestamp'], 10);
        } else {
          // Then try to extract from SuprArtData filename
          timeA = getTimestampFromFileName(a.name);
        }
        
        if (b.metadata && b.metadata['original-timestamp']) {
          timeB = parseInt(b.metadata['original-timestamp'], 10);
        } else {
          // Then try to extract from SuprArtData filename
          timeB = getTimestampFromFileName(b.name);
        }
        
        // Fallback to lastModified if no timestamp found
        if (!timeA || isNaN(timeA)) {
          timeA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
        }
        if (!timeB || isNaN(timeB)) {
          timeB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
        }
      }
      
      // Sort by time (newest first by default for desc)
      return direction * (timeB - timeA);
    }
    
    // Size sorting (same for all buckets)
    if (sortConfig.field === 'size') {
      return direction * ((a.size || 0) - (b.size || 0));
    }
    
    return 0;
  });

  // Sort folders
  const sortedFolders = [...folders].sort((a, b) => {
    // Archive folders always come first
    if (a.isArchive && !b.isArchive) return -1;
    if (!a.isArchive && b.isArchive) return 1;
    
    // For non-archive folders, sort by name
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    return direction * a.name.localeCompare(b.name);
  });

  /**
   * Handle bulk selection with warnings
   */
  const handleSelectAll = (e) => {
    // Direct selection without warnings for now
    onSelectAll(e);
  };

  // Check if an item is selected
  const isSelected = (item) =>
    selectedFiles.some((selected) => selected.path === item.path);

/**
 * Gets a formatted time string from file metadata or name
 * @param {Object} file - File object
 * @returns {string} Formatted time string
 */
const getTimeDisplay = (file) => {
  // Profile bucket - use lastModified timestamp
  if (bucketType === 'profile') {
    if (file.lastModified) {
      try {
        const date = new Date(file.lastModified);
        if (date.toString() !== 'Invalid Date') {
          return formatDate(date);
        }
      } catch (error) {
        // console.error('Error parsing lastModified timestamp:', error);
      }
    }
    return "--";
  }
  
  // Video bucket - extract timestamp from filename
  if (bucketType === 'video') {
    const videoMatch = file.name.match(/-(\d{13})\.mp4$/);
    if (videoMatch) {
      try {
        const timestamp = parseInt(videoMatch[1], 10);
        const date = new Date(timestamp);
        if (date.toString() !== 'Invalid Date') {
          return formatDate(date);
        }
      } catch (error) {
        // console.error('Error parsing video timestamp:', error);
      }
    }
    // Fallback to lastModified for videos without timestamp in name
    if (file.lastModified) {
      try {
        const date = new Date(file.lastModified);
        if (date.toString() !== 'Invalid Date') {
          return formatDate(date);
        }
      } catch (error) {
        // console.error('Error parsing lastModified:', error);
      }
    }
    return "--";
  }
  
  // Live and Artwork buckets - check metadata first
  if (file.metadata && file.metadata['original-timestamp']) {
    try {
      const timestamp = parseInt(file.metadata['original-timestamp'], 10);
      if (!isNaN(timestamp)) {
        const date = new Date(timestamp);
        if (date.toString() !== 'Invalid Date') {
          return formatDate(date);
        }
      }
    } catch (error) {
      // console.error('Error parsing timestamp from metadata:', error);
    }
  }

  // Try to extract from filename (SuprArtData files)
  const formattedName = formatFileName(file.name);
  if (formattedName !== file.name) {
    return formattedName;
  }

  // Final fallback to lastModified
  if (file.lastModified) {
    try {
      const date = new Date(file.lastModified);
      if (date.toString() !== 'Invalid Date') {
        return formatDate(date);
      }
    } catch (error) {
      // console.error('Error parsing lastModified:', error);
    }
  }

  return "--";
};

// Table cell styling for consistent alignment
const cellStyles = {
  headerCell: {
    fontWeight: 600,
    paddingLeft: 0,
    paddingRight: 0,
    color: 'text.secondary',
    fontSize: '0.85rem',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  documentNameCell: {
    paddingLeft: 2,
    paddingRight: 2,
    minWidth: { xs: '150px', sm: '200px', md: '250px' },
    maxWidth: { xs: '250px', sm: '350px', md: '450px' },
    fontWeight: 500,
  },
  timeCell: {
    paddingLeft: 2,
    paddingRight: 2,
    minWidth: { xs: '180px', sm: '220px', md: '220px' },
    color: 'text.secondary',
    fontSize: { xs: '0.75rem', sm: '0.85rem' },
  },
  sizeCell: {
    paddingLeft: 2,
    paddingRight: 2,
    width: { xs: '100px', sm: '120px' },
    textAlign: 'left',
    color: 'text.secondary',
    fontSize: { xs: '0.75rem', sm: '0.85rem' },
  },
  folderCell: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 1, sm: 1.5 },
    width: '100%',
  },
  fileCell: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 1, sm: 1.5 },
    width: '100%',
  },
  checkboxCell: {
    width: { xs: '40px', sm: '48px' },
    paddingLeft: { xs: 1, sm: 2 },
    paddingRight: { xs: 1, sm: 2 },
  },
  tableSortLabel: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
    paddingLeft: 0,
    paddingRight: 0,
    textAlign: 'left',
    '& .MuiTableSortLabel-icon': {
      opacity: 0.5,
      marginLeft: '4px',
      marginRight: 0,
    },
  },
  tableRow: {
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      backgroundColor: 'background.elevated',
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(88, 101, 242, 0.12)',
      '&:hover': {
        backgroundColor: 'rgba(88, 101, 242, 0.16)',
      },
    },
  },
};

return (
  <Box sx={{ 
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  }}>
    {/* Bucket Type Header */}
    <Box sx={{ 
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: 2,
      px: { xs: 2, sm: 3 },
      borderBottom: 1,
      borderColor: 'rgba(255, 255, 255, 0.05)',
      backgroundColor: 'background.paper',
      backdropFilter: 'blur(8px)',
      borderRadius: 0,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: 0.5,
          }}
        >
          {bucketType === 'live' ? (
            selectedGroup === 'all' ? 'All Files' : selectedGroup
          ) : bucketType === 'artwork' ? 'Artwork' :
            bucketType === 'video' ? 'Videos' :
            bucketType === 'profile' ? 'Gallery' : ''
          }
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            fontSize: '0.7rem',
          }}
        >
          •
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          {folders.length} folder{folders.length !== 1 ? 's' : ''}, {filesProp.length} file{filesProp.length !== 1 ? 's' : ''}
          {selectedFiles.length > 0 && ` • ${selectedFiles.length} selected`}
        </Typography>
      </Box>
    </Box>

    {/* Loading Progress Bar */}
    {loading && (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 2,
        px: { xs: 2, sm: 3 },
      }}>
        <LinearProgress
          sx={{
            width: { xs: '100%', sm: '80%', md: '600px' },
            maxWidth: '600px',
            height: 6,
            borderRadius: '4px',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: bucketType === 'live' ? '#81C9C4' :
                               bucketType === 'artwork' ? '#E8A5B5' :
                               bucketType === 'video' ? '#B5D99C' : '#81C9C4'
            }
          }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Loading files...
        </Typography>
      </Box>
    )}

    {/* Mobile list view */}
    <Box 
      sx={{ 
        display: { xs: 'block', sm: 'none' },
        flex: 1,
        overflow: 'hidden',
        px: 1,
        py: 1,
        height: '100%'
      }}
      ref={mobileContainerRef}
    >
      {sortedFolders.length + sortedFiles.length > 0 ? (
        <List
          height={mobileListHeight}
          itemCount={sortedFolders.length + sortedFiles.length}
          itemSize={80} // Height of each mobile row
          width="100%"
          itemData={{
            items: [...sortedFolders, ...sortedFiles],
            selectedFiles,
            onSelect,
            onFolderClick,
            bucketType,
            isSelected,
            getDocumentName,
            getTimeDisplay,
            formatSize,
            getFileIcon
          }}
          overscanCount={3} // Render 3 extra rows outside of visible area
        >
          {VirtualizedMobileRow}
        </List>
      ) : (
        // Empty state
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          py: 8,
        }}>
          <FolderIcon 
            sx={{ 
              fontSize: '3rem', 
              color: 'text.disabled',
              mb: 2, 
              opacity: 0.5 
            }} 
          />
          <Typography variant="h6" color="text.secondary">
            This folder is empty
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
            Upload files or create a new folder
          </Typography>
        </Box>
      )}
    </Box>

    {/* Desktop table view */}
    <Box
      sx={{
        display: { xs: 'none', sm: 'block' },
        flex: 1,
        overflow: 'hidden',
        px: { xs: 1, sm: 0 },
        pt: 2,
        pb: 0,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          backgroundColor: 'transparent',
          overflow: 'hidden',
          borderRadius: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Table Header */}
        <Box sx={{ flexShrink: 0, overflowX: 'auto' }}>
          <Table sx={{
            tableLayout: 'auto',
            width: '100%',
            '& .MuiTableCell-root': {
              borderColor: 'rgba(255, 255, 255, 0.05)',
            },
          }}>
            <TableHead>
              <TableRow sx={{ display: 'flex', width: '100%' }}>
                <TableCell 
                  padding="checkbox" 
                  sx={{
                    ...cellStyles.checkboxCell,
                    ...cellStyles.headerCell,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Checkbox
                    indeterminate={selectedFiles.length > 0 && !allSelected}
                    checked={allSelected}
                    onChange={handleSelectAll}
                    sx={{
                      color: 'text.disabled',
                      '&.Mui-checked': {
                        color: `${getBucketColor(bucketType)}.main`,
                      },
                      '&.MuiCheckbox-indeterminate': {
                        color: `${getBucketColor(bucketType)}.main`,
                      },
                    }}
                  />
                </TableCell>
                
                {/* Document Name Column */}
                <TableCell sx={{
                  ...cellStyles.headerCell,
                  flex: '1 1 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  overflow: 'hidden',
                }}>
                  <TableSortLabel
                    active={sortConfig.field === 'document_name'}
                    direction={sortConfig.direction}
                    onClick={() => onSort('document_name')}
                    sx={cellStyles.tableSortLabel}
                    IconComponent={ArrowUpwardIcon}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                
                {/* Time Column */}
                <TableCell sx={{
                  ...cellStyles.headerCell,
                  width: { xs: '180px', sm: '220px', md: '220px' },
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}>
                  <TableSortLabel
                    active={sortConfig.field === 'name'}
                    direction={sortConfig.direction}
                    onClick={() => onSort('name')}
                    sx={{
                      ...cellStyles.tableSortLabel,
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    }}
                    IconComponent={ArrowUpwardIcon}
                  >
                    Time
                  </TableSortLabel>
                </TableCell>

                {/* Size Column */}
                <TableCell sx={{
                  ...cellStyles.headerCell,
                  width: { xs: '100px', sm: '120px' },
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}>
                  <TableSortLabel
                    active={sortConfig.field === 'size'}
                    direction={sortConfig.direction}
                    onClick={() => onSort('size')}
                    sx={{
                      ...cellStyles.tableSortLabel,
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    }}
                    IconComponent={ArrowUpwardIcon}
                  >
                    Size
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </Box>

        {/* Virtualized Table Body */}
        <Box sx={{ flex: 1, overflow: 'auto' }} ref={containerRef}>
          {sortedFolders.length + sortedFiles.length > 0 ? (
            <List
              height={listHeight}
              itemCount={sortedFolders.length + sortedFiles.length}
              itemSize={76} // Height includes row spacing (60px + 16px padding)
              width="100%"
              itemData={{
                items: [...sortedFolders, ...sortedFiles],
                selectedFiles,
                onSelect,
                onFolderClick,
                bucketType,
                cellStyles,
                isSelected,
                getDocumentName,
                getTimeDisplay,
                formatSize,
                getFileIcon
              }}
              overscanCount={5} // Render 5 extra rows outside of visible area
            >
              {VirtualizedRow}
            </List>
          ) : (
            // Empty state
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              py: 8,
            }}>
              <FolderIcon 
                sx={{ 
                  fontSize: '3rem', 
                  color: 'text.disabled',
                  mb: 2, 
                  opacity: 0.5 
                }} 
              />
              <Typography variant="h6" color="text.secondary">
                This folder is empty
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                Upload files or create a new folder
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  </Box>
);
}, (prevProps, nextProps) => {
  // Custom comparison function - return true if props are equal (skip re-render)
  // Compare arrays by length and reference (assuming new arrays are created on actual changes)
  return (
    prevProps.files === nextProps.files &&
    prevProps.folders === nextProps.folders &&
    prevProps.selectedFiles === nextProps.selectedFiles &&
    prevProps.allSelected === nextProps.allSelected &&
    prevProps.sortConfig.field === nextProps.sortConfig.field &&
    prevProps.sortConfig.direction === nextProps.sortConfig.direction &&
    prevProps.bucketType === nextProps.bucketType &&
    prevProps.loading === nextProps.loading
  );
});

export default FileGrid;